import React, { useState } from "react";
import { Popover, Button, Group, Text } from "@mantine/core";
import { DatePicker } from "@mantine/dates";
import useStyles from "./style";
import moment from "moment";
import { MdOutlineCalendarToday } from "react-icons/md";
import { useMediaQuery } from "@mantine/hooks";

const EffectivePeriodDatePicker = ({
  dateRange,
  setDateRange,
  handleApply,
  open,
  opened,
  close,
  value,
  setTargetState,
  disabled,
}) => {
  const { classes } = useStyles();
  const isExtraSmallScreen = useMediaQuery("(max-width: 834px)");
  const isMediumScreen = useMediaQuery("(max-width:992px)");
  const [isDateRangeValid, setIsDateRangeValid] = useState(false);

  const handleRange = (dates) => {
    if (dates && dates[0] && dates[1]) {
      const startOfDay = new Date(dates[0]).setHours(0, 0, 0, 0);
      const endOfDay = new Date(dates[1]).setHours(23, 59, 59, 999);

      const adjustedDates = [new Date(startOfDay), new Date(endOfDay)];

      setDateRange({ type: "custom", date: adjustedDates });
      setIsDateRangeValid(true);
    } else {
      setDateRange({ type: "custom", date: dates });
      setIsDateRangeValid(false);
    }
  };

  const handleCustomRange = () => {
    setIsDateRangeValid(false);
    setDateRange({ type: "custom", date: [] });
  };

  const handleClose = () => {
    setDateRange({ type: "", date: [] });
    setTargetState([]);
    setIsDateRangeValid(false);
    close();
  };

  const handleTillYear = () => {
    const today = new Date();
    const currentYear = today.getFullYear();
    const lastDayOfYear = new Date(currentYear, 11, 31);

    setDateRange({
      type: "date till year",
      date: [today, lastDayOfYear],
    });
    setIsDateRangeValid(true);
  };

  const handleSixMonths = () => {
    const today = new Date();
    const currentYear = today.getFullYear();
    const currentMonth = today.getMonth();

    const firstDayCurrentMonth = new Date(currentYear, currentMonth, 1);
    firstDayCurrentMonth.setHours(0, 0, 0, 0);
    const futureMonth = new Date(currentYear, currentMonth + 6, 0);
    futureMonth.setHours(23, 59, 59, 999);

    setDateRange({
      type: "6 months",
      date: [firstDayCurrentMonth, futureMonth],
    });
    setIsDateRangeValid(true);
  };

  const handleTwelveMonths = () => {
    const today = new Date();
    const currentYear = today.getFullYear();
    const currentMonth = today.getMonth();

    // First day of current month
    const firstDayCurrentMonth = new Date(currentYear, currentMonth, 1);
    firstDayCurrentMonth.setHours(0, 0, 0, 0);

    // Calculate the last day of the month that is 11 months ahead
    const futureMonth = new Date(currentYear, currentMonth + 12, 0);
    futureMonth.setHours(23, 59, 59, 999);

    setDateRange({
      type: "12 months",
      date: [firstDayCurrentMonth, futureMonth],
    });
    setIsDateRangeValid(true);
  };

  // Handler for selecting current month + next 17 months (total 18 months)
  const handleEighteenMonths = () => {
    const today = new Date();
    const currentYear = today.getFullYear();
    const currentMonth = today.getMonth();

    // First day of current month
    const firstDayCurrentMonth = new Date(currentYear, currentMonth, 1);
    firstDayCurrentMonth.setHours(0, 0, 0, 0);

    // Calculate the last day of the month that is 17 months ahead
    const futureMonth = new Date(currentYear, currentMonth + 18, 0);
    futureMonth.setHours(23, 59, 59, 999);

    setDateRange({
      type: "18 months",
      date: [firstDayCurrentMonth, futureMonth],
    });
    setIsDateRangeValid(true);
  };

  // Handler for selecting current month + next 23 months (total 24 months)
  const handleTwentyFourMonths = () => {
    const today = new Date();
    const currentYear = today.getFullYear();
    const currentMonth = today.getMonth();

    // First day of current month, start of day
    const firstDayCurrentMonth = new Date(currentYear, currentMonth, 1);
    firstDayCurrentMonth.setHours(0, 0, 0, 0);

    // Last day of the month that is 23 months ahead (total 24 months)
    const futureMonth = new Date(currentYear, currentMonth + 24, 0);
    futureMonth.setHours(23, 59, 59, 999);

    setDateRange({
      type: "24 months",
      date: [firstDayCurrentMonth, futureMonth],
    });
    setIsDateRangeValid(true);
  };

  // Handler for selecting from current date to end of current quarter
  const handleDateToCurrentQuarter = () => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const currentYear = today.getFullYear();
    const currentMonth = today.getMonth();

    // Determine the current quarter's end month (0-11)
    let quarterEndMonth;

    if (currentMonth <= 2) {
      // Q1: January (0) to March (2)
      quarterEndMonth = 2;
    } else if (currentMonth <= 5) {
      // Q2: April (3) to June (5)
      quarterEndMonth = 5;
    } else if (currentMonth <= 8) {
      // Q3: July (6) to September (8)
      quarterEndMonth = 8;
    } else {
      // Q4: October (9) to December (11)
      quarterEndMonth = 11;
    }

    // Create date for the last day of the current quarter
    const lastDayOfQuarter = new Date(currentYear, quarterEndMonth + 1, 0);
    lastDayOfQuarter.setHours(23, 59, 59, 999);

    setDateRange({
      type: "date to quarter",
      date: [today, lastDayOfQuarter],
    });
    setIsDateRangeValid(true);
  };

  const isActive = (type) => dateRange.type === type;

  return (
    <Group justify="end">
      <Popover
        shadow="md"
        onClose={close}
        styles={{
          dropdown: {
            zIndex: "1100 !important",
          },
        }}
        opened={opened}
        width={isExtraSmallScreen ? 310 : 450}
        position={isMediumScreen ? "bottom" : "left"}
        className={classes.datePickerPopOver}
        middlewares={{ flip: false, shift: false, inline: false }}
      >
        <Popover.Target>
          <Button
            className={classes.buttonStyle}
            onClick={open}
            disabled={disabled}
          >
            <MdOutlineCalendarToday className={classes.iconCalendar} />
            {`${
              value.length > 0 && value[0] && value[1]
                ? `${moment(value[0]).format("ll")} - ${moment(value[1]).format(
                    "ll"
                  )}`
                : "Select dates range"
            }`}
          </Button>
        </Popover.Target>
        <Popover.Dropdown className={classes.popOverCard}>
          <div className={classes.datePickerParent}>
            <div className={classes.buttonsParent}>
              <Button
                className={`${classes.dateButtons} ${
                  isActive("custom") ? classes.activeButton : ""
                }`}
                onClick={() => {
                  handleCustomRange();
                }}
              >
                Custom
              </Button>
              <Button
                onClick={() => handleSixMonths()}
                className={`${classes.dateButtons} ${
                  isActive("6 months") ? classes.activeButton : ""
                }`}
              >
                6 month
              </Button>
              <Button
                onClick={() => handleTwelveMonths()}
                className={`${classes.dateButtons} ${
                  isActive("12 months") ? classes.activeButton : ""
                }`}
              >
                12 month
              </Button>
              <Button
                onClick={() => handleEighteenMonths()}
                className={`${classes.dateButtons} ${
                  isActive("18 months") ? classes.activeButton : ""
                }`}
              >
                18 month
              </Button>
              <Button
                onClick={() => handleTwentyFourMonths()}
                className={`${classes.dateButtons} ${
                  isActive("24 months") ? classes.activeButton : ""
                }`}
              >
                24 month
              </Button>
              <Button
                onClick={() => handleTillYear()}
                className={`${classes.dateButtons} ${
                  isActive("date till year") ? classes.activeButton : ""
                }`}
              >
                Date Till Year
              </Button>
              <Button
                onClick={() => handleDateToCurrentQuarter()}
                className={`${classes.dateButtons} ${
                  isActive("date to quarter") ? classes.activeButton : ""
                }`}
              >
                Date To Quarter
              </Button>
            </div>
            <div className={classes.datePickerParentDiv}>
              <Text color="var(--app-text)" ff="Inter" fz={14} fw={500} ta="center">
                Select Effective Period
              </Text>
              <DatePicker
                type="range"
                onChange={handleRange}
                value={dateRange?.date}
                label="Select dates range"
                placeholder="Select dates range"
                className={classes.datePicker}
                styles={{
                  day: {
                    "&[data-in-range]": {
                      backgroundColor: "var(--app-primary) !important",
                      color: "var(--app-on-primary) !important",
                    },
                  },
                }}
              />
              <div className={classes.bottomButtonsParent}>
                <Button
                  onClick={handleClose}
                  className={`${classes.bottomButtons} ${classes.cancelButton}`}
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleApply}
                  disabled={!isDateRangeValid}
                  className={`${classes.bottomButtons} ${classes.applyButton}`}
                >
                  Apply
                </Button>
              </div>
            </div>
          </div>
        </Popover.Dropdown>
      </Popover>
    </Group>
  );
};

export default EffectivePeriodDatePicker;
